/*
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import React, { useCallback, useEffect, useState } from 'react';
import appTheme from '../../common/theme/appTheme.json';
import { Box, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import CustomDataGrid from '../../common/data_grid/CustomDataGrid';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import ModalDialog from '../../common/dialogs/ModalDialog';
import WarningIcon from '@mui/icons-material/Warning';
import { Link, useRouteMatch } from 'react-router-dom';
import ErrorMessage from '../../common/layout/ErrorMessage';
import { StyledProvisioningPageHeading } from '../StyledProvisioningPageHeading';
import ModalLoadingSpinner from '../../common/layout/ModalLoadingSpinner';
import { selectClients, selectClientsRequestError, selectIsRequestingClients } from '../../../store';
import CreateIcon from '@mui/icons-material/Create';
import { RenderTopAlignedFixedHeightCell } from '../../common/data_grid/TopAlignedCell';
import { deleteClient, requestClients } from '../../../store/clients/actions';
import { Client } from '../../../shared/entities/client';
import { cleanupRoles } from '../users/UsersTable';
import { TopAlignedChipCell } from '../../common/data_grid/ChipCell';

interface ClientsTableProps {
  // Required for displaying tables in unit tests
  disableVirtualization?: boolean;
}

const ClientsTable = ({ disableVirtualization = false }: ClientsTableProps) => {
  const dispatch = useDispatch();
  const { path } = useRouteMatch();
  const loadingClients = useSelector(selectIsRequestingClients, shallowEqual);
  const clientsArray = useSelector(selectClients, shallowEqual);
  const clientsRequestError = useSelector(selectClientsRequestError, shallowEqual);
  const [deleteModalTarget, setDeleteModalTarget] = useState<Client | undefined>(undefined);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    dispatch(requestClients());
  }, [dispatch]);

  const COLLAPSED_ROW_HEIGHT_PX = 49;
  const columns: GridColDef[] = [
    {
      field: 'clientId',
      headerName: 'Client ID',
      flex: 1,
      renderCell: RenderTopAlignedFixedHeightCell(COLLAPSED_ROW_HEIGHT_PX),
    },
    {
      field: 'name',
      headerName: 'Client name',
      flex: 1,
      renderCell: RenderTopAlignedFixedHeightCell(COLLAPSED_ROW_HEIGHT_PX),
    },
    {
      field: 'description',
      headerName: 'Client description',
      flex: 2,
      renderCell: RenderTopAlignedFixedHeightCell(COLLAPSED_ROW_HEIGHT_PX),
    },
    {
      field: 'roles',
      headerName: 'Client role(s)',
      flex: 2,
      renderCell: params => {
        cleanupRoles(params);
        return <TopAlignedChipCell params={params} />;
      },
      sortable: false,
    },
    {
      field: 'orgName',
      headerName: 'Organization',
      flex: 1,
      renderCell: RenderTopAlignedFixedHeightCell(COLLAPSED_ROW_HEIGHT_PX),
    },
    {
      type: 'actions',
      field: 'actions',
      cellClassName: 'ActionsCell-TopAligned',
      headerName: '',
      align: 'right',
      flex: 1,
      getActions: params => [
        <GridActionsCellItem
          key={'create-client'}
          icon={<CreateIcon />}
          component={Link}
          to={`${path}/edit/${params.row.clientId}`}
        />,
        <GridActionsCellItem
          key={'delete-client'}
          icon={<DeleteIcon />}
          onClick={() => {
            setDeleteModalTarget(params.row);
            setShowDeleteModal(true);
          }}
        />,
      ],
    },
  ];

  const AddClientButton = useCallback(
    () => (
      <Button
        component={Link}
        to={`${path}/new`}
        startIcon={<AddIcon />}
        variant="contained"
        sx={{
          marginLeft: 'auto',
          color: '#000000',
          letterSpacing: '0.8px',
          backgroundColor: appTheme.mptLightBlue,
          '&:hover': {
            backgroundColor: appTheme.mptLightBlue,
            svg: {
              color: '#000000',
            },
          },
        }}
      >
        Provision new client
      </Button>
    ),
    [path],
  );

  return (
    <>
      {clientsRequestError ? <ErrorMessage>{clientsRequestError}</ErrorMessage> : null}

      <StyledProvisioningPageHeading>
        Clients {clientsArray ? `(${clientsArray.length})` : ''}
      </StyledProvisioningPageHeading>

      {loadingClients ? <ModalLoadingSpinner /> : null}

      <Box display="flex" justifyContent="space-between" flexDirection={'column'} height={'100%'}>
        <CustomDataGrid
          rows={clientsArray || []}
          getRowHeight={() => 'auto'}
          getEstimatedRowHeight={() => COLLAPSED_ROW_HEIGHT_PX}
          getRowId={row => row.clientId}
          columns={columns}
          initialState={{
            sorting: {
              sortModel: [{ field: 'clientName', sort: 'asc' }],
            },
          }}
          toolbarActionButtons={[<AddClientButton key={'add-client-button'} />]}
          slotProps={{
            toolbar: {
              csvOptions: {
                fileName: 'Clients',
              },
            },
          }}
          disableVirtualization={disableVirtualization}
        />
      </Box>

      <ModalDialog
        open={showDeleteModal}
        onLeftBtnClicked={() => {
          setShowDeleteModal(false);
        }}
        onRightBtnClicked={() => {
          dispatch(deleteClient(deleteModalTarget!.clientId));
          setShowDeleteModal(false);
        }}
        onExited={() => {
          // Remove the delete target after the dialog's exit animation has finished. Otherwise, the `message` below
          // will briefly display `undefined` for the organization's display name
          setDeleteModalTarget(undefined);
        }}
        title={
          <Box display={'flex'} gap={'8px'} alignItems={'center'}>
            <WarningIcon htmlColor={'#FFB029'} /> Confirm Client Deletion
          </Box>
        }
        message={
          <>
            Are you sure you want to delete the client <code>{deleteModalTarget?.name}</code>?
            <br />
            <br />
            Deleting a client will remove its access to UP.
            <br />
            To completely remove this client, you must also remove its client id from Okta.
            <br />
            <br />
            This action cannot be undone.
          </>
        }
      />
    </>
  );
};

export default ClientsTable;
